import { useCallback, useEffect, useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  FileText,
  Calendar,
  Truck,
  Building,
  ShoppingCart,
  Check,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { Order, OrderLine, Product } from '../types'
import {
  formatCurrency,
  formatDate,
  getOrderStatusLabel,
  getOrderStatusColor,
} from '../utils/helpers'
import LineItemsTable from '../components/LineItemsTable'
import { useCart } from '../hooks/useCart'

export default function OrderDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const erpId = searchParams.get('erp_id') ? Number(searchParams.get('erp_id')) : undefined
  const [order, setOrder] = useState<Order | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [addedAll, setAddedAll] = useState(false)
  const { addToCart } = useCart()

  // Build a minimal Product from an order line for adding to cart
  const lineToProduct = (line: OrderLine): Product => ({
    id: line.product_id || line.product_ref || line.id || '',
    ref: line.product_ref || '',
    label: line.product_label || line.description,
    price: line.unit_price,
    price_ttc: line.unit_price * (1 + line.vat_rate / 100),
    vat_rate: line.vat_rate,
    type: 0,
    status: 1,
  })

  const handleAddLineToCart = (line: OrderLine, qty: number) => {
    if (!line.product_ref && !line.product_id) return
    addToCart(lineToProduct(line), qty)
  }

  const handleAddOrderToCart = () => {
    const lines = order?.lines ?? []
    lines.forEach((line) => {
      if (line.product_ref || line.product_id) {
        addToCart(lineToProduct(line), line.qty)
      }
    })
    setAddedAll(true)
    setTimeout(() => setAddedAll(false), 2000)
  }

  const loadOrder = useCallback(async () => {
    if (!id) return
    try {
      const data = await api.getOrder(id, erpId)
      setOrder(data)
    } catch (error) {
      console.error('Erreur chargement commande:', error)
    } finally {
      setLoading(false)
    }
  }, [id, erpId])

  useEffect(() => {
    loadOrder()
  }, [loadOrder])

  const handleDownloadPDF = async () => {
    if (!id) return

    setDownloading(true)
    try {
      const pdfData = await api.downloadOrderPDF(id, erpId)

      if (pdfData) {
        // Créer un blob depuis le base64
        const byteCharacters = atob(pdfData)
        const byteNumbers = new Array(byteCharacters.length)
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        const blob = new Blob([byteArray], { type: 'application/pdf' })

        // Créer un lien de téléchargement
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `${order?.ref || 'commande'}.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        alert(t('orderDetail.pdfError'))
      }
    } catch (error) {
      console.error('Erreur téléchargement PDF:', error)
      alert(t('orderDetail.pdfDownloadError'))
    } finally {
      setDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!order) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('orderDetail.notFound')}</h2>
        <Link to="/orders" className="text-primary-600 hover:text-primary-700">
          {t('orderDetail.backToList')}
        </Link>
      </div>
    )
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link to="/orders" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('orderDetail.title', { ref: order.ref })}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {t('orderDetail.createdOn', { date: formatDate(order.date) })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {order?.lines && order.lines.some((l) => l.product_ref || l.product_id) && (
            <button
              onClick={handleAddOrderToCart}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${addedAll ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
            >
              {addedAll ? <Check className="w-5 h-5" /> : <ShoppingCart className="w-5 h-5" />}
              <span>{addedAll ? t('catalog.addedToCart') : t('orderDetail.addOrderToCart')}</span>
            </button>
          )}
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-5 h-5" />
            <span>{downloading ? t('orderDetail.downloading') : t('orderDetail.downloadPdf')}</span>
          </button>
        </div>
      </div>

      {/* Status badges */}
      <div className="flex items-center gap-2 flex-wrap">
        <span
          className={`inline-block px-3 py-1 text-sm font-medium rounded-full ${getOrderStatusColor(order.status)}`}
        >
          {getOrderStatusLabel(order.status, t)}
        </span>
        <span
          className={`px-3 py-1 text-sm font-medium rounded-full ${
            order.is_billed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
          }`}
        >
          {order.is_billed ? t('orderDetail.billed') : t('orderDetail.notBilled')}
        </span>
      </div>

      {/* Main Info Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{t('orderDetail.generalInfo')}</h2>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Customer name + address */}
          {(order.customer_name || order.customer_address) && (
            <div className="flex items-start space-x-3 md:col-span-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('orderDetail.customer')}</p>
                {order.customer_name && (
                  <p className="font-medium text-gray-900">{order.customer_name}</p>
                )}
                {order.customer_address && (
                  <p className="text-sm text-gray-600">{order.customer_address}</p>
                )}
                {(order.customer_zip || order.customer_city) && (
                  <p className="text-sm text-gray-600">
                    {[order.customer_zip, order.customer_city].filter(Boolean).join(' ')}
                  </p>
                )}
                {order.customer_country && (
                  <p className="text-sm text-gray-500">{order.customer_country}</p>
                )}
              </div>
            </div>
          )}

          {/* Date de commande */}
          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('orderDetail.orderDate')}</p>
              <p className="font-medium text-gray-900">{formatDate(order.date)}</p>
            </div>
          </div>

          {/* Date de création */}
          {order.date_creation && (
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('orderDetail.creationDate')}</p>
                <p className="font-medium text-gray-900">{formatDate(order.date_creation)}</p>
              </div>
            </div>
          )}

          {/* Date de livraison */}
          {order.delivery_date && (
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('orderDetail.deliveryDate')}</p>
                <p className="font-medium text-gray-900">{formatDate(order.delivery_date)}</p>
              </div>
            </div>
          )}

          {/* Référence client */}
          {order.customer_ref && (
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('orderDetail.customerRef')}</p>
                <p className="font-medium text-gray-900">{order.customer_ref}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Amounts Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 flex flex-col gap-1 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('orderDetail.totalExclTax')}</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(order.total_excl_tax)}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">{t('orderDetail.vat')}</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(order.total_incl_tax - order.total_excl_tax)}
            </span>
          </div>
          <div className="flex items-center justify-between pt-1 border-t border-gray-100 mt-1">
            <span className="font-semibold text-gray-900">{t('orderDetail.totalInclTax')}</span>
            <span className="font-bold text-gray-900">{formatCurrency(order.total_incl_tax)}</span>
          </div>
        </div>
      </div>

      {/* Line items */}
      {order.lines !== undefined && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{t('lineItems.title')}</h2>
          </div>
          <div className="p-6">
            <LineItemsTable
              lines={order.lines ?? []}
              onAddToCart={(line, qty) => handleAddLineToCart(line as OrderLine, qty)}
            />
          </div>
        </div>
      )}

      {/* Notes */}
      {order.notes && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{t('orderDetail.notes')}</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 whitespace-pre-wrap">{order.notes}</p>
          </div>
        </div>
      )}
    </div>
  )
}
